import {
  LazyQueryHookOptions,
  LazyQueryResultTuple,
  OperationVariables,
} from '@apollo/client';
import { TFunction } from 'i18next';
import { getDateRangeBounds } from 'modules/dashboard/contexts/PhlebotomistDashboardContext/usePhlebotomistStatsData';
import { DateRangeType } from 'modules/dashboard/types/phlebotomist-dashboard';
import { useEffect } from 'react';

import {
  IDateBounds,
  IGenericBarItem,
  IModalFilterContext,
  IStatusLegendItem,
  PeriodType,
} from '../types';
import { getHistoryConfig } from '../utils/getHistoryConfig';
import { getBreakpointColor } from '../utils/getThresholdStatus';

export interface IMetricHistoryExtraInput {
  repeatDonationsThreshold?: number;
  donorDonationFrequency?: number;
  donorEligibilityDays?: number;
  historyDataCutoff?: string;
}

interface IBuildVariablesParams {
  filter: IModalFilterContext;
  periodType: PeriodType;
  bars: number;
  anchorBounds: IDateBounds;
  extra: IMetricHistoryExtraInput;
}

export interface IMetricHistoryConfig<
  TData,
  TVariables extends OperationVariables,
  TPeriod extends { label: string },
  TLive
> {
  /** Generated Apollo lazy hook */
  useLazyQuery: (
    baseOptions?: LazyQueryHookOptions<TData, TVariables>
  ) => LazyQueryResultTuple<TData, TVariables>;
  /** Pull the period array out of the query response. */
  selectPeriods: (data: TData | undefined) => TPeriod[];
  /** Build the typed query variables for this metric. */
  buildVariables: (params: IBuildVariablesParams) => TVariables;
  /** Live (card) values merged into the most recent bar. */
  getLiveFields: (live: TLive) => Partial<TPeriod>;
  /** Whether the most recent bar should reflect live card data. */
  resolveUseLiveLast: (
    dateRange: DateRangeType,
    isQuarterly: boolean
  ) => boolean;
  /** Numeric value plotted for a (possibly merged) period. */
  getValue: (period: TPeriod) => number;
  /** Direction used to colour bars against the thresholds. */
  higherIsBetter: boolean;
  /** Threshold breakpoints (from live data or a constant). */
  getBreakpoints: (live: TLive) => [number, number];
  /** Tooltip lines for a (possibly merged) period. */
  buildTooltipLines: (period: TPeriod, t: TFunction) => string[];
  /** Build the history-chart legend items for this metric. */
  buildLegend: (
    t: TFunction,
    live: TLive,
    higherIsBetter: boolean
  ) => IStatusLegendItem[];
  /** Override anchor bounds (dropout anchors on "now"); defaults to date range. */
  getAnchorBounds?: (filter: IModalFilterContext) => IDateBounds;
}

interface IUseMetricHistoryArgs<TLive> {
  live: TLive;
  filter: IModalFilterContext;
  extra: IMetricHistoryExtraInput;
  t: TFunction;
  isQuarterly?: boolean;
}

export interface IMetricHistoryResult {
  items: IGenericBarItem[];
  loading: boolean;
  periodType: PeriodType;
}

export function useMetricHistory<
  TData,
  TVariables extends OperationVariables,
  TPeriod extends { label: string },
  TLive
>(
  config: IMetricHistoryConfig<TData, TVariables, TPeriod, TLive>,
  { live, filter, extra, t, isQuarterly = false }: IUseMetricHistoryArgs<TLive>
): IMetricHistoryResult {
  const [exec, { data, loading }] = config.useLazyQuery();

  const { periodType, bars } = getHistoryConfig(filter.dateRange, isQuarterly);
  const anchorBounds =
    config.getAnchorBounds?.(filter) ??
    getDateRangeBounds(filter.dateRange, filter.customDateRange);

  useEffect(() => {
    exec({
      variables: config.buildVariables({
        filter,
        periodType,
        bars,
        anchorBounds,
        extra,
      }),
    });
  }, [periodType, bars]);

  const breakpoints = config.getBreakpoints(live);
  const useLiveLast = config.resolveUseLiveLast(filter.dateRange, isQuarterly);
  const periods = config.selectPeriods(data);

  const items: IGenericBarItem[] = periods.map((period, i) => {
    const isLatest = useLiveLast && i === periods.length - 1;
    const merged: TPeriod = isLatest
      ? { ...period, ...config.getLiveFields(live) }
      : period;
    const value = config.getValue(merged);
    return {
      label: merged.label,
      value,
      color: getBreakpointColor(value, breakpoints, config.higherIsBetter),
      tooltipLines: config.buildTooltipLines(merged, t),
    };
  });

  return { items, loading, periodType };
}
